import React from "react";
import "./FamilyMedicalHistoryOverview.css";

const getText = (value) => {
  if (value == null) return "";

  if (typeof value === "string") {
    return value.trim();
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const cleanOverviewText = (value) =>
  getText(value)
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "family member"
    )
    .replace(/\b[a-f0-9]{20,}\b/gi, "family member")
    .replace(/\s+/g, " ")
    .trim();

export default function FamilyMedicalHistoryOverview({
  report,
  loading = false,
  onClose,
}) {
  /* ============================================================
     AI DATA
  ============================================================ */

  const findings = Array.isArray(
    report?.deterministicFindings
  )
    ? report.deterministicFindings
    : [];

  const inheritedRisks = Array.isArray(
    report?.llmReport?.inheritedRiskPerspective
  )
    ? report.llmReport.inheritedRiskPerspective
    : [];

  /* ============================================================
     SUMMARY
  ============================================================ */

  const aiSummary =
    cleanOverviewText(report?.llmReport?.summary) ||
    cleanOverviewText(report?.summary);

  const fallbackSummary =
    "The family medical history provides an overview of the health background of the user and their connected family members. It includes parents, children, and spouse relationships, helping organize relevant medical information within one family tree.";

  const summary = aiSummary || fallbackSummary;

  /* ============================================================
     MEDICAL HISTORY
  ============================================================ */

  const medicalHistoryText =
    "The available family history can be reviewed to identify previous illnesses, chronic conditions, allergies, medications, surgeries, and other significant health events reported by family members.";

  /* ============================================================
     FAMILY HEALTH PATTERNS
  ============================================================ */

  const familyHealthPatternsText =
    "Analyzing the history across generations may help highlight potential hereditary conditions or recurring health patterns that could be relevant for future medical consultations.";

  /* ============================================================
     CONCLUSION
  ============================================================ */

  const explicitConclusion =
    cleanOverviewText(
      report?.llmReport?.conclusion
    ) ||
    cleanOverviewText(report?.conclusion);

  const signalsCount = Math.max(
    findings.length,
    inheritedRisks.length
  );

  const conclusion =
    explicitConclusion ||
    (signalsCount > 0
      ? `The generated family analysis identified ${signalsCount} family health signal${
          signalsCount > 1 ? "s" : ""
        }. These results should be reviewed with a healthcare professional together with the available family medical history.`
      : "The available family information provides a structured overview of the medical history. No significant familial risk signal was highlighted in the generated summary, but incomplete information may still require medical review.");

  return (
    <aside
      className="family-medical-overview"
      aria-live="polite"
    >
      {/* ======================================================
          FIXED HEADER
      ====================================================== */}

      <div className="family-medical-overview__header">
        <h3 className="family-medical-overview__title">
          Family Medical
          <br />
          History Overview
        </h3>

        <button
          type="button"
          className="family-medical-overview__close"
          onClick={onClose}
          aria-label="Close family medical history overview"
        >
          ×
        </button>
      </div>

      {/* ======================================================
          SCROLLABLE BODY
      ====================================================== */}

      <div className="family-medical-overview__scroll">
        {loading ? (
          <div className="family-medical-overview__loading">
            <span className="family-medical-overview__spinner" />

            <p>
              Generating family medical overview...
            </p>
          </div>
        ) : (
          <>
            {/* SUMMARY */}

            <div className="family-medical-overview__content">
              <p>{summary}</p>

              {/* MEDICAL HISTORY */}

              <div className="family-medical-overview__section">
                <span className="family-medical-overview__section-title">
                  Medical History:
                </span>

                <p>{medicalHistoryText}</p>
              </div>

              {/* FAMILY HEALTH PATTERNS */}

              <div className="family-medical-overview__section">
                <span className="family-medical-overview__section-title">
                  Family Health Patterns:
                </span>

                <p>{familyHealthPatternsText}</p>
              </div>
            </div>

            {/* CONCLUSION */}

            <div className="family-medical-overview__conclusion">
              <span className="family-medical-overview__conclusion-label">
                Conclusion:
              </span>

              <p>{conclusion}</p>
            </div>

            {/* DISCLAIMER */}

            {report?.medicalDisclaimer ? (
              <p className="family-medical-overview__disclaimer">
                {cleanOverviewText(
                  report.medicalDisclaimer
                )}
              </p>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
}